import { forwardRef, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@restworld/utils-common';

export type EmailInputVariant = 'primary' | 'error';

export interface EmailInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Current value */
  value?: string;
  /** Label displayed above the input */
  label?: string | React.ReactNode;
  /** Support / error text displayed below the input */
  supportText?: string | React.ReactNode;
  /** Visual variant */
  variant?: EmailInputVariant;
  /** Called with the raw ChangeEvent */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Called on blur with the raw ChangeEvent */
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Test ID for data-cy attribute */
  testId?: string;
  /** Test ID for the support text */
  supportTextTestId?: string;
  /** Additional classes for the container */
  containerClassName?: string;
  /** Override suffix icon */
  suffixIcon?: React.ReactNode;
}

export const EmailInput = forwardRef<HTMLInputElement, EmailInputProps>(
  (
    {
      value,
      label,
      supportText,
      variant = 'primary',
      onChange,
      onBlur,
      placeholder,
      disabled,
      testId,
      supportTextTestId,
      id,
      containerClassName,
      suffixIcon,
      ...rest
    },
    ref
  ) => {
    const isErrored = variant === 'error';

    return (
      <div className={cn('flex flex-col', containerClassName)}>
        {label ? <p className="mb-2 text-base font-semibold leading-6">{label}</p> : null}
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type="email"
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            data-cy={testId}
            className={cn(
              'block w-full rounded-lg border border-neutral-90 bg-neutral-10 px-2 py-4 h-12 text-base',
              'placeholder:!text-neutral-200 placeholder:truncate placeholder:text-base placeholder:font-normal',
              'focus:border-primary-300 focus:ring-primary-300 focus:ring-1 focus:outline-none',
              'hover:bg-neutral-30',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-40',
              isErrored && '!bg-red-50 border-red-500 text-red-700 placeholder:!text-red-700',
              (isErrored || suffixIcon) && 'pr-10'
            )}
            {...rest}
          />
          {isErrored ? (
            <ExclamationCircleIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-red-700" />
          ) : null}
        </div>
        {supportText || isErrored ? (
          <p
            data-cy={supportTextTestId}
            className={cn('!text-sm font-normal mt-2', isErrored ? 'text-red-600' : 'text-neutral-200')}
          >
            {isErrored ? supportText || "L'email inserita non è valida, ricontrolla e riprova" : supportText}
          </p>
        ) : null}
      </div>
    );
  }
);

EmailInput.displayName = 'EmailInput';
